import { safeParse as vSafeParse } from 'valibot';
import dayjs from 'dayjs';
import type { PropertyOnList } from '#shared/property/api-types';
import type { PropertyOnCard } from '#shared/property/types';
import { MARKETING_STORAGE_KEY, MarketingStorageSchema } from '#shared/lead/marketing';
import type { TrackingEvent } from '~/application';

export function trackEvent(trackingEvent: TrackingEvent) {
  if (!Array.isArray(window.dataLayer)) return;
  // Make sure we don't pass empty values to the dataLayer
  const cleanedTrackingEvents = getCleanedTrackingEvent(trackingEvent);
  // Never send email or phone to the dataLayer directly
  // We need to hash the email and phone before sending them to the dataLayer
  if (trackingEvent.h_email || trackingEvent.h_phone) {
    trackEventAsync(cleanedTrackingEvents, window.dataLayer);
  }
  else {
    window.dataLayer.push(cleanedTrackingEvents);
  }
};

async function trackEventAsync(cleanedTrackingEvents: TrackingEventFormatted, dataLayer: Array<unknown>) {
  const [email, phone] = await Promise.allSettled([
    cleanedTrackingEvents.h_email ? digestMessage(cleanedTrackingEvents.h_email) : undefined,
    cleanedTrackingEvents.h_phone ? digestMessage(cleanedTrackingEvents.h_phone) : undefined,
  ]);
  cleanedTrackingEvents.h_email = email.status === 'fulfilled' ? email.value : undefined;
  cleanedTrackingEvents.h_phone = phone.status === 'fulfilled' ? phone.value : undefined;
  dataLayer.push(cleanedTrackingEvents);
}

type TrackingEventFormatted = {
  [K in keyof TrackingEvent]: K extends 'event' | 'event_name' | 'h_email' | 'h_phone' ? string : string | number
};

function getCleanedTrackingEvent(trackingEvent: TrackingEvent): TrackingEventFormatted {
  const cleanedTrackingEvents: TrackingEventFormatted = {
    event: trackingEvent.event,
  };
  Object.entries(trackingEvent).forEach(([key, value]) => {
    if (typeof value === 'undefined' || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;
    const _value = Array.isArray(value) ? value.join('|') : value;
    cleanedTrackingEvents[key] = _value;
  });
  return cleanedTrackingEvents;
}

// https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
async function digestMessage(message: string): Promise<string> {
  const msgUint8 = new TextEncoder().encode(message);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
}

export function getTrackingPropertyCategory(property?: PropertyOnList | PropertyOnCard): Array<string> | undefined {
  if (!property) return;
  const propertyCategory = [
    property.isExclusive ? 'exclusive' : undefined,
    ...property.tags,
  ].filter(item => typeof item === 'string');
  return propertyCategory;
}

export function getMarketingTracking() {
  const marketingTracking = localStorage.getItem(MARKETING_STORAGE_KEY);
  if (!marketingTracking) return;
  try {
    const parsedData = JSON.parse(marketingTracking);
    const result = vSafeParse(MarketingStorageSchema, parsedData);
    if (!result.success) return;
    const expiresAt = dayjs.unix(result.output.expiresAt);
    const isStillValid = dayjs().isBefore(expiresAt);
    if (!isStillValid) return;
    return result.output.parameters;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-empty
  catch (error) { }
}
